import React, { useEffect, useMemo, useState } from "react";
import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";
import useSondageQuestion from "../../../hooks/useSondageQuestion";
import "../../../assets/css/sondage/AdminSondageQuestion.css";

const SECTIONS = [
  { key: "questionPourVendeur", label: "Vendeur" },
  { key: "questionPourArticle", label: "Article" },
];

const TYPES_REPONSE = [
  { value: 1, label: "Texte libre" },
  { value: 2, label: "Choix unique" },
  { value: 3, label: "Choix multiple" },
];

const typeLabel = (t) =>
  TYPES_REPONSE.find((x) => x.value === Number(t))?.label ?? "Inconnu";

const typePillClass = (t) => {
  switch (Number(t)) {
    case 2:
      return "pillPremium";
    case 3:
      return "pillStandard";
    case 4:
      return "pillGood";
    default:
      return "pillNeutral";
  }
};

const needsChoix = (t) => [2, 3].includes(Number(t));

const emptySondage = { questionPourVendeur: [], questionPourArticle: [] };

export default function AdminSondageQuestion({ setAlert }) {
  const {
    getAllSondageQuestions,
    createSondageQuestion,
    updateSondageQuestion,
  } = useSondageQuestion();

  // Le document config (celui qui contient questionPourVendeur / questionPourArticle)
  const [doc, setDoc] = useState(emptySondage);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // UI state
  const [activeSection, setActiveSection] = useState(SECTIONS[0].key);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // all | 1 | 2 | 3 | 4
  const [sort, setSort] = useState("order"); // order | question_asc | question_desc
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // modal form
  const [modalOpen, setModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);

  const emptyForm = {
    question: "",
    typeReponse: 1,
    choix: [],
  };
  const [form, setForm] = useState(emptyForm);
  const [choixDraft, setChoixDraft] = useState("");

  const breadcrumbItems = [
    { label: "Mon Compte", link: "/utilisateur/compte" },
    { label: "Admin", link: "/admin/dashboard" },
    { label: "Sondages" },
  ];

  const fetchDoc = async () => {
    setLoading(true);
    try {
      const res = await getAllSondageQuestions();
      const list = Array.isArray(res?.data)
        ? res.data
        : (res?.data?.data ?? []);
      // On garde toujours le doc[0] existant, avec son _id
      setDoc(list[0] || emptySondage);
    } catch (err) {
      console.error(err);
      setAlert?.({
        type: "danger",
        message: "Impossible de charger les questions de sondage.",
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchDoc();
  }, []);

const persist = async (updatedDoc) => {
  setSaving(true);
  try {
    const hasExistingDoc = Boolean(doc?._id); // toujours se baser sur le doc chargé, pas sur updatedDoc
    const payload = hasExistingDoc ? { ...updatedDoc, _id: doc._id } : updatedDoc;

    const res = hasExistingDoc
      ? await updateSondageQuestion(doc._id, payload)
      : await createSondageQuestion(payload);

    const saved = res?.data?.data ?? res?.data ?? payload;
    setDoc(saved);
    return true;
  } catch (err) {
    console.error(err);
    setAlert?.({ type: "danger", message: "Échec de l'enregistrement." });
    return false;
  } finally {
    setSaving(false);
  }
};

  const openCreate = () => {
    setEditIndex(null);
    setForm(emptyForm);
    setChoixDraft("");
    setModalOpen(true);
  };

  const openEdit = (item, origIndex) => {
    setEditIndex(origIndex);
    setForm({
      question: item?.question ?? "",
      typeReponse: Number(item?.typeReponse ?? 1),
      choix: [...(item?.choix ?? [])],
    });
    setChoixDraft("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  const currentItems = doc?.[activeSection] || [];

  const filtered = useMemo(() => {
    const nq = String(q || "")
      .toLowerCase()
      .trim();

    return currentItems
      .map((item, i) => ({ ...item, _origIndex: i }))
      .filter((item) => {
        const okQ =
          !nq ||
          String(item?.question || "")
            .toLowerCase()
            .includes(nq);
        const okType =
          typeFilter === "all"
            ? true
            : Number(item?.typeReponse) === Number(typeFilter);
        return okQ && okType;
      })
      .sort((a, b) => {
        if (sort === "order") return a._origIndex - b._origIndex;

        const qa = String(a?.question || "").toLowerCase();
        const qb = String(b?.question || "").toLowerCase();
        if (sort === "question_asc") return qa.localeCompare(qb);
        if (sort === "question_desc") return qb.localeCompare(qa);

        return 0;
      });
  }, [currentItems, q, typeFilter, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  useEffect(() => {
    setPage(1);
  }, [q, typeFilter, sort, activeSection]);

  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page]);

  const addChoix = () => {
    const value = choixDraft.trim();
    if (!value) return;
    setForm((f) => ({ ...f, choix: [...f.choix, value] }));
    setChoixDraft("");
  };

  const removeChoix = (index) => {
    setForm((f) => ({ ...f, choix: f.choix.filter((_, i) => i !== index) }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const question = {
      question: String(form.question || "").trim(),
      typeReponse: Number(form.typeReponse),
      choix: needsChoix(form.typeReponse) ? form.choix : [],
    };

    if (!question.question) {
      setAlert?.({
        type: "warning",
        message: "Le champ 'question' est requis.",
      });
      return;
    }
    if (needsChoix(question.typeReponse) && question.choix.length < 2) {
      setAlert?.({
        type: "warning",
        message: "Ajoute au moins deux choix pour ce type de réponse.",
      });
      return;
    }

    const nextItems = [...currentItems];
    if (editIndex !== null) {
      nextItems[editIndex] = { ...nextItems[editIndex], ...question };
    } else {
      nextItems.push(question);
    }

    const ok = await persist({ ...doc, [activeSection]: nextItems });
    if (ok) {
      setAlert?.({
        type: "success",
        message:
          editIndex !== null
            ? "Question modifiée avec succès."
            : "Question créée avec succès.",
      });
      setModalOpen(false);
      setForm(emptyForm);
      setEditIndex(null);
    }

    fetchDoc();
  };

  const onDelete = async (item, origIndex) => {
    const ok = window.confirm(
      `Supprimer la question "${item?.question || ""}" ?`,
    );
    if (!ok) return;

    const nextItems = currentItems.filter((_, i) => i !== origIndex);
    const success = await persist({ ...doc, [activeSection]: nextItems });
    if (success) {
      setAlert?.({ type: "success", message: "Question supprimée." });
    }
  };

  return (
    <div className="adminSondagePage">
      <div className="container">
        <Breadcrumb items={breadcrumbItems} />

        <div className="adminGroupHeader">
          <div>
            <div className="adminGroupTitle">Questions de sondage</div>
            <div className="adminGroupSubtitle">
              {loading
                ? "Chargement..."
                : "Gérer les questions posées aux clients pour évaluer les vendeurs et les articles."}
            </div>
          </div>

          <div className="adminGroupActions">
            <button
              className="adminBtn adminBtnSoft"
              type="button"
              onClick={fetchDoc}
              disabled={loading}
              title="Rafraîchir"
            >
              <i className="bi bi-arrow-clockwise" /> Rafraîchir
            </button>

            <button
              className="adminBtn adminBtnPrimary"
              type="button"
              onClick={openCreate}
            >
              <i className="bi bi-plus-circle" /> Nouvelle question
            </button>
          </div>
        </div>

        {/* Sections (vendeur / article) */}
        <div className="adminSondageTabs">
          {SECTIONS.map((section) => (
            <button
              key={section.key}
              type="button"
              className={`adminSondageTab ${activeSection === section.key ? "adminSondageTabActive" : ""}`}
              onClick={() => setActiveSection(section.key)}
            >
              {section.label}
              <span className="adminSondageTabCount">
                {doc?.[section.key]?.length ?? 0}
              </span>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="adminGroupFilters adminSondageFilters">
          <div className="filterItem">
            <label>Recherche</label>
            <input
              className="form-control"
              placeholder="Rechercher par question…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <div className="filterItem">
            <label>Type de réponse</label>
            <select
              className="form-select"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="all">Tous</option>
              {TYPES_REPONSE.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filterItem">
            <label>Tri</label>
            <select
              className="form-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="order">Ordre d’origine</option>
              <option value="question_asc">Question (A→Z)</option>
              <option value="question_desc">Question (Z→A)</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="adminGroupTableWrap">
          {loading ? (
            <div className="adminEmpty">Chargement…</div>
          ) : filtered.length === 0 ? (
            <div className="adminEmpty">Aucune question trouvée.</div>
          ) : (
            <table className="table adminGroupTable">
              <thead>
                <tr>
                  <th>Question</th>
                  <th style={{ width: 160 }}>Type de réponse</th>
                  <th>Choix</th>
                  <th style={{ width: 110 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((item) => (
                  <tr key={item._id ?? item._origIndex}>
                    <td>
                      <div className="rowTitle">{item?.question || "—"}</div>
                    </td>

                    <td>
                      <span
                        className={`pill ${typePillClass(item?.typeReponse)}`}
                      >
                        {typeLabel(item?.typeReponse)}
                      </span>
                    </td>

                    <td>
                      <span className="rowMuted">
                        {item?.choix?.length ? item.choix.join(", ") : "—"}
                      </span>
                    </td>

                    <td>
                      <div className="rowActions">
                        <button
                          className="btnAction"
                          onClick={() => openEdit(item, item._origIndex)}
                          title="Modifier"
                        >
                          <i className="bi bi-pencil-square" />
                        </button>

                        <button
                          className="btnAction btnDanger"
                          onClick={() => onDelete(item, item._origIndex)}
                          title="Supprimer"
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {filtered.length > 0 && (
          <div className="adminPagination">
            <button
              className="adminBtn adminBtnSoft"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <i className="bi bi-chevron-left" /> Précédent
            </button>

            <div className="pageInfo">
              Page <strong>{page}</strong> / {totalPages} • {filtered.length}{" "}
              résultat(s)
            </div>

            <button
              className="adminBtn adminBtnSoft"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Suivant <i className="bi bi-chevron-right" />
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="adminModalOverlay" onClick={closeModal}>
          <div className="adminModal" onClick={(e) => e.stopPropagation()}>
            <div className="adminModalHeader">
              <div className="adminModalTitle">
                {editIndex !== null
                  ? "Modifier la question"
                  : "Créer une question"}
              </div>
              <button
                className="adminModalClose"
                onClick={closeModal}
                disabled={saving}
                title="Fermer"
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <form onSubmit={onSubmit}>
              <div className="adminModalBody">
                <div className="mb-3">
                  <label className="form-label fw-bold">Question</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={form.question}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, question: e.target.value }))
                    }
                    placeholder="Ex: Le vendeur a-t-il répondu rapidement ?"
                  />
                </div>

                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-bold">
                      Type de réponse
                    </label>
                    <select
                      className="form-select"
                      value={form.typeReponse}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          typeReponse: Number(e.target.value),
                        }))
                      }
                    >
                      {TYPES_REPONSE.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {needsChoix(form.typeReponse) && (
                  <div className="mb-3 mt-3">
                    <label className="form-label fw-bold">Choix proposés</label>
                    <div className="d-flex gap-2 mb-2">
                      <input
                        className="form-control"
                        value={choixDraft}
                        onChange={(e) => setChoixDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addChoix();
                          }
                        }}
                        placeholder="Ajouter un choix"
                      />
                      <button
                        type="button"
                        className="adminBtn adminBtnSoft"
                        onClick={addChoix}
                      >
                        Ajouter
                      </button>
                    </div>

                    {form.choix.length > 0 ? (
                      <div className="adminSondageChoixList">
                        {form.choix.map((c, i) => (
                          <span
                            key={i}
                            className="pill pillNeutral adminSondageChoixPill"
                          >
                            {c}
                            <button
                              type="button"
                              className="adminSondageChoixRemove"
                              onClick={() => removeChoix(i)}
                              title="Retirer"
                            >
                              <i className="bi bi-x" />
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="rowMuted">
                        Aucun choix pour l’instant.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="adminModalFooter">
                <button
                  type="button"
                  className="adminBtn adminBtnSoft"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Annuler
                </button>

                <button
                  type="submit"
                  className="adminBtn adminBtnPrimary"
                  disabled={saving}
                >
                  {saving
                    ? "Enregistrement…"
                    : editIndex !== null
                      ? "Modifier"
                      : "Créer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
